import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useBudget } from '../App';

const AddTransaction = () => {
  const { t, isRTL } = useTranslation();
  const { addTransaction, categories, currency, setCurrency } = useBudget();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    type: 'expense',
    amount: '',
    category: 'food',
    description: '',
    date: new Date().toISOString().split('T')[0],
    currency: currency
  });

  const [newCategory, setNewCategory] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);

  const currencies = ['YER', 'USD', 'SAR'];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      alert(isRTL ? 'يرجى إدخال مبلغ صحيح' : 'Please enter a valid amount');
      return;
    }

    addTransaction({
      ...formData,
      amount: parseFloat(formData.amount)
    });

    // Success animation
    const button = e.target.querySelector('button[type="submit"]');
    button.classList.add('bg-green-500');
    button.textContent = isRTL ? '✓ تم الحفظ' : '✓ Saved';
    
    setTimeout(() => {
      navigate('/');
    }, 1000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addNewCategory = () => {
    if (newCategory.trim()) {
      // Add to local state for immediate use
      const customCategory = newCategory.trim().toLowerCase();
      setFormData(prev => ({
        ...prev,
        category: customCategory
      }));
      setNewCategory('');
      setShowNewCategory(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '';
    const formattedAmount = new Intl.NumberFormat(isRTL ? 'ar-YE' : 'en-US').format(amount);
    return `${formattedAmount} ${t(`currencies.${formData.currency}`)}`;
  };

  return (
    <div className="max-w-2xl mx-auto slide-in">
      <div className="card">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {formData.type === 'income' ? t('transaction.add_income') : t('transaction.add_expense')}
          </h2>
          <p className="text-gray-600">
            {isRTL ? 'أضف معاملة جديدة إلى ميزانيتك' : 'Add a new transaction to your budget'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Transaction Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isRTL ? 'نوع المعاملة' : 'Transaction Type'}
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, type: 'income' }))}
                className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                  formData.type === 'income'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-300 hover:border-green-300'
                }`}
              >
                <div className="text-2xl mb-2">💰</div>
                <div className="font-semibold">{t('transaction.income')}</div>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, type: 'expense' }))}
                className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                  formData.type === 'expense'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-300 hover:border-red-300'
                }`}
              >
                <div className="text-2xl mb-2">💸</div>
                <div className="font-semibold">{t('transaction.expense')}</div>
              </button>
            </div>
          </div>

          {/* Amount and Currency */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('transaction.amount')}
              </label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                className="form-input text-xl font-semibold"
                placeholder={isRTL ? 'أدخل المبلغ' : 'Enter amount'}
                min="0"
                step="0.01"
                required
              />
              {formData.amount && (
                <p className="text-sm text-gray-500 mt-1">
                  {formatCurrency(formData.amount)}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('transaction.currency')}
              </label>
              <select
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
                className="form-input"
              >
                {currencies.map(curr => (
                  <option key={curr} value={curr}>
                    {t(`currencies.${curr}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('transaction.category')}
            </label>
            <div className="space-y-2">
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="form-input"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {t(`categories.${cat}`)}
                  </option>
                ))}
              </select>
              
              {!showNewCategory ? (
                <button
                  type="button"
                  onClick={() => setShowNewCategory(true)}
                  className="text-blue-500 hover:text-blue-700 text-sm"
                >
                  + {isRTL ? 'إضافة فئة جديدة' : 'Add new category'}
                </button>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="form-input flex-1"
                    placeholder={isRTL ? 'اسم الفئة الجديدة' : 'New category name'}
                  />
                  <button
                    type="button"
                    onClick={addNewCategory}
                    className="btn-primary"
                  >
                    {t('buttons.add')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewCategory(false);
                      setNewCategory('');
                    }}
                    className="btn-secondary"
                  >
                    {t('transaction.cancel')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('transaction.description')} ({isRTL ? 'اختياري' : 'optional'})
            </label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="form-input"
              placeholder={isRTL ? 'وصف المعاملة' : 'Transaction description'}
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('transaction.date')}
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              className="form-input"
              required
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              className="btn-primary flex-1 py-3 text-lg font-semibold transform hover:scale-105"
            >
              💾 {t('transaction.save')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="btn-secondary flex-1 py-3 text-lg"
            >
              {t('transaction.cancel')}
            </button>
          </div>
        </form>
      </div>

      {/* Quick Actions */}
      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="card text-center bg-gradient-to-r from-green-100 to-green-200">
          <div className="text-3xl mb-2">💰</div>
          <p className="text-sm text-green-700">
            {isRTL ? 'نصيحة: سجل دخلك بانتظام' : 'Tip: Record income regularly'}
          </p>
        </div>
        <div className="card text-center bg-gradient-to-r from-blue-100 to-blue-200">
          <div className="text-3xl mb-2">📊</div>
          <p className="text-sm text-blue-700">
            {isRTL ? 'تابع إنفاقك يومياً' : 'Track spending daily'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AddTransaction;